import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { modalManagerToken } from "@/tokens";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import { reviewsIconCollectionManagerToken } from "@apps/reviews/tokens";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import { productsServiceToken } from "@modules/products/tokens";
import { create } from "superstruct";
import { container, injectable } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
import ReviewsDrawerWidgetComponent from "./ReviewsDrawerWidget.svelte";
import {
    ReviewsDrawerWidgetConfigInterface,
    ReviewsDrawerWidgetConfigSchema,
} from "./ReviewsDrawerWidgetConfig";

@injectable()
class ReviewsDrawerWidget implements WidgetInterface {
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly productsService: ProductsServiceInterface;
    private readonly modalManager: ModalManager;
    private readonly reviewsGalleryManager: ReviewsGalleryManager;
    private readonly dynamicImportManager: DynamicImportManager;
    private readonly i18nModule: I18nModule;

    constructor(
        private readonly config: ReviewsDrawerWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        // I18n
        this.i18nModule = container.resolve(I18nModule);
        this.i18nModule.exports.i18n.registerLoader(
            "reviews.ReviewsDrawerWidget",
            this.i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ReviewsDrawerWidget"
            )
        );
        this.i18nModule.exports.i18n.registerLoader(
            "reviews.critical.ReviewsDrawerWidget",
            this.i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "reviews.critical.ReviewsDrawerWidget"
            )
        );

        this.config = create(config, ReviewsDrawerWidgetConfigSchema);
        this.logger.debug("ReviewsDrawerWidget is initialized", config.target);

        // Products Service
        this.productsService = container.resolve(productsServiceToken);
        this.productsService.subscribe();

        this.modalManager = container.resolve(modalManagerToken);
        this.reviewsGalleryManager = container.resolve(ReviewsGalleryManager);
        this.dynamicImportManager =
            container.resolve(DynamicImportModule).exports.dynamicImportManager;
    }

    async init(): Promise<this> {
        const element = this.config.target;
        const customizationManager = container.resolve(CustomizationManager);

        let placeholder = element.querySelector<HTMLElement>(
            ".gw-reviews-drawer-widget-placeholder"
        );

        if (!placeholder) {
            placeholder = document.createElement("div");
            placeholder.classList.add("gw-reviews-drawer-widget-placeholder");
            placeholder.style.display = "block";
            element.appendChild(placeholder);
        }

        const shadowRoot =
            placeholder.shadowRoot ||
            placeholder.attachShadow({ mode: "open" });

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_DRAWER_WIDGET,
            placeholder,
            shadowRoot,
        };

        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );

        new ReviewsDrawerWidgetComponent({
            target: shadowRoot,
            props: {
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                customizationManager,
                widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_DRAWER_WIDGET,
                modalManager: this.modalManager,
                reviewsGalleryManager: this.reviewsGalleryManager,
                dynamicImportManager: this.dynamicImportManager,
                logger: this.logger,
                i18n: this.i18nModule.exports.i18n,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
            context: contextMap,
        });

        return this;
    }
}

export default ReviewsDrawerWidget;
